import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Alert,
  AlertDescription,
  AlertIcon,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  useAddNegotiationRoundInviteMutation,
  useGetOrganizationQuery,
  useRemoveNegotiationRoundInviteMutation,
  useUpdateNegotiationRoundInviteMessageMutation,
  type NegotiationRoundDto,
  type NegotiationRoundPatchDto,
  type SourcingEventDto,
} from "../../../../autogen/bff-api";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { ListLoadError } from "../../../../common/ListLoadError";
import { StepNavigation } from "../../../../common/StepNavigation";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { reservesStagedReduction, stagedReductionProvision } from "../../../../common/procurement/procedure";
import { type RoundInviteCandidate } from "../../rounds/roundInviteCandidates";
import { allowsPartIIDialogue } from "../../rounds/partIIDialogue";
import {
  selectionJustificationMissing,
  useDialogueRoster,
} from "../../rounds/useDialogueRoster";

interface Props {
  event: SourcingEventDto;
  round: NegotiationRoundDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

interface MessageDraft {
  organizationId: string;
  personIds: string[];
  message: string;
  isNew: boolean;
}

export const RoundInvitationsStep = ({
  event,
  round,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const confirmationModal = useConfirmationModal();

  const [addInvite] = useAddNegotiationRoundInviteMutation();
  const [removeInvite] = useRemoveNegotiationRoundInviteMutation();
  const [updateInviteMessage] =
    useUpdateNegotiationRoundInviteMessageMutation();

  const [messageDraft, setMessageDraft] = useState<MessageDraft | null>(null);
  const [removalAwaitingReason, setRemovalAwaitingReason] = useState<{
    organizationId: string;
    reason: string;
  } | null>(null);
  const [reductionAwaitingReason, setReductionAwaitingReason] = useState<{
    organizationId: string;
    reason: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  const roster = useDialogueRoster(event, round);
  const { candidates, leavesOutTenderers, tenderersLoading, tenderersFailed } =
    roster;
  const reducesInStages =
    event.eventContext.type === "Public" &&
    reservesStagedReduction(event.eventContext);
  const leavesOnlyByRejection =
    event.eventContext.type === "Public" &&
    !allowsPartIIDialogue(event) &&
    !reducesInStages &&
    [round, ...roster.rounds].some(
      (r) => r.status !== NegotiationRoundStatus.Draft,
    );
  const removalLockedBecause = leavesOnlyByRejection
    ? t("A supplier leaves the negotiation only by being rejected.")
    : reducesInStages && round.status !== NegotiationRoundStatus.Draft
      ? t("Suppliers are taken out between rounds, in the next round before it is published.")
      : undefined;
  const { state: editedRound, updateField, flushPending } = useEditor<NegotiationRoundDto, NegotiationRoundPatchDto>(round.id);
  const [justification, setJustification] = useState(
    round.selectionJustification ?? "",
  );
  const invitedIds = new Set(
    round.invites.map((invite) => invite.organizationId),
  );
  const leftOutIds = candidates
    .map((candidate) => candidate.organizationId)
    .filter(
      (id) =>
        !invitedIds.has(id) && !roster.rejectedOrganizationIds.includes(id),
    );
  const didNotAnswer = roster.didNotAnswerPreviousRound;
  const justificationSuggestion =
    didNotAnswer &&
    leftOutIds.length > 0 &&
    leftOutIds.every((id) => didNotAnswer.organizationIds.includes(id))
      ? t("The suppliers left out did not answer round {{number}}.", {
          number: didNotAnswer.roundNumber,
        })
      : undefined;

  const isPublished = round.status === NegotiationRoundStatus.Published;
  const canEditMessages = round.status === NegotiationRoundStatus.Draft;
  const roundInviteFor = (organizationId: string) =>
    round.invites.find((invite) => invite.organizationId === organizationId);

  const saveOnce = async (save: () => Promise<void>) => {
    if (saving.current) return;
    saving.current = true;
    setIsSaving(true);
    try {
      await save();
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  const performAdd = (draft: MessageDraft) =>
    saveOnce(async () => {
      try {
        await addInvite({
          eventId: event.id,
          roundId: round.id,
          addNegotiationRoundInviteRequest: {
            organizationId: draft.organizationId,
            personIds: draft.personIds,
            message: draft.message || null,
          },
        }).unwrap();
        setMessageDraft(null);
      } catch (e) {
        displayer.trigger(e);
      }
    });

  const performMessageUpdate = (draft: MessageDraft) =>
    saveOnce(async () => {
      try {
        await updateInviteMessage({
          eventId: event.id,
          roundId: round.id,
          pathOrganizationId: draft.organizationId,
          updateNegotiationRoundInviteMessageRequest: {
            message: draft.message || null,
          },
        }).unwrap();
        setMessageDraft(null);
      } catch (e) {
        displayer.trigger(e);
      }
    });

  const performRemove = async (
    organizationId: string,
    selectionJustification?: string,
    reductionReason?: string,
  ): Promise<boolean> => {
    try {
      await removeInvite({
        eventId: event.id,
        roundId: round.id,
        pathOrganizationId: organizationId,
        selectionJustification,
        reductionReason,
      }).unwrap();
      return true;
    } catch (e) {
      displayer.trigger(e);
      return false;
    }
  };

  const saveDraft = (draft: MessageDraft) => {
    if (draft.isNew && isPublished) {
      confirmationModal.show({
        title: t("Confirm invite"),
        description: t(
          "You are live, and the selected organization will immediately be invited to the event",
        ),
        onConfirmed: () => void performAdd(draft),
        confirmButtonColor: "teal",
        confirmButtonText: t("Create") ?? "",
      });
    } else if (draft.isNew) {
      void performAdd(draft);
    } else {
      void performMessageUpdate(draft);
    }
  };

  const removalNeedsReason = (organizationId: string) =>
    isPublished &&
    allowsPartIIDialogue(event) &&
    !(round.selectionJustification ?? "").trim() &&
    candidates.some(
      (candidate) => candidate.organizationId === organizationId,
    ) &&
    !roster.rejectedOrganizationIds.includes(organizationId);

  const removeGivingReason = (organizationId: string, reason: string) =>
    saveOnce(async () => {
      if (await performRemove(organizationId, reason.trim())) {
        setJustification(reason.trim());
        setRemovalAwaitingReason(null);
      }
    });

  const reduceGivingReason = (organizationId: string, reason: string) =>
    saveOnce(async () => {
      if (await performRemove(organizationId, undefined, reason.trim())) {
        setReductionAwaitingReason(null);
      }
    });

  const removeWithConfirmation = (organizationId: string) => {
    if (reducesInStages && canEditMessages && roster.reducibleOrganizationIds.includes(organizationId)) {
      setReductionAwaitingReason({ organizationId, reason: "" });
    } else if (removalNeedsReason(organizationId)) {
      setRemovalAwaitingReason({ organizationId, reason: "" });
    } else if (isPublished) {
      confirmationModal.show({
        title: t("Confirm removal"),
        description: t(
          "{{supplier}} is removed from negotiation round {{number}} at once, and can no longer answer it.",
          {
            supplier: roundInviteFor(organizationId)?.organizationName,
            number: round.roundNumber,
          },
        ),
        onConfirmed: () => void performRemove(organizationId),
        confirmButtonColor: "red",
        confirmButtonText: t("Remove") ?? "",
      });
    } else {
      void performRemove(organizationId);
    }
  };

  const saveJustification = (): boolean => {
    const trimmed = justification.trim();
    if (trimmed === (editedRound.selectionJustification ?? "")) return false;
    updateField({ selectionJustification: trimmed || null });
    return true;
  };

  const nextBlockedBecause = !canEditMessages
    ? null
    : tenderersLoading || tenderersFailed
      ? t("The suppliers who tendered are not loaded yet")
      : selectionJustificationMissing(roster, justification)
        ? t("Write why not every supplier who tendered is invited")
        : null;

  const goNext = nextStep
    ? async () => {
        if (saveJustification()) {
          try {
            await flushPending();
          } catch (e) {
            displayer.trigger(e);
            return;
          }
        }
        nextStep();
      }
    : undefined;

  const openDraftFor = (candidate: RoundInviteCandidate) => {
    const existing = roundInviteFor(candidate.organizationId);
    setMessageDraft({
      organizationId: candidate.organizationId,
      personIds: candidate.personIds,
      message: existing?.message ?? "",
      isNew: !existing,
    });
  };

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Invitations")}
      </Heading>
      <Text fontSize="sm" color="smMuted" pb="4">
        {t(
          "Choose which of the event's organizations are invited to this round.",
        )}
      </Text>

      {confirmationModal.modal}

      <Modal
        isOpen={!!removalAwaitingReason}
        onClose={() => setRemovalAwaitingReason(null)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Remove from the live round")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {removalAwaitingReason && (
              <>
                <Text mb="4">
                  <OrgName
                    organizationId={removalAwaitingReason.organizationId}
                  />{" "}
                  {t(
                    "is removed from the round at once and is no longer in dialogue with you in it. A tenderer still in the competition can only be left out with a reason, which is written in the procurement protocol.",
                  )}
                </Text>
                <SelectionJustification
                  value={removalAwaitingReason.reason}
                  onChange={(reason) =>
                    setRemovalAwaitingReason({
                      ...removalAwaitingReason,
                      reason,
                    })
                  }
                  onBlur={() => undefined}
                  isDisabled={false}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={() => setRemovalAwaitingReason(null)}
            >
              {t("Cancel")}
            </Button>
            <Button
              colorScheme="teal"
              isLoading={isSaving}
              isDisabled={!removalAwaitingReason?.reason.trim()}
              onClick={() =>
                removalAwaitingReason &&
                void removeGivingReason(
                  removalAwaitingReason.organizationId,
                  removalAwaitingReason.reason,
                )
              }
            >
              {t("Remove")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ReductionModal
        pending={reductionAwaitingReason}
        suppliersLeft={round.invites.length - 1}
        provision={
          event.eventContext.type === "Public" ? stagedReductionProvision(event.eventContext) : null
        }
        isSaving={isSaving}
        onChange={setReductionAwaitingReason}
        onCancel={() => setReductionAwaitingReason(null)}
        onConfirm={(pending) =>
          void reduceGivingReason(pending.organizationId, pending.reason)
        }
      />

      <Modal isOpen={!!messageDraft} onClose={() => setMessageDraft(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {messageDraft?.isNew
              ? t("Add Invitation Message")
              : t("Edit Invitation Message")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {messageDraft && (
              <>
                <Text mb="2">
                  {t("Message to")}:{" "}
                  <OrgName organizationId={messageDraft.organizationId} />
                </Text>
                <Textarea
                  value={messageDraft.message}
                  onChange={(e) =>
                    setMessageDraft({
                      ...messageDraft,
                      message: e.target.value,
                    })
                  }
                  placeholder={`${t("Enter message for the organization")}`}
                  rows={5}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={() => setMessageDraft(null)}
            >
              {t("Cancel")}
            </Button>
            <Button
              colorScheme="teal"
              isLoading={isSaving}
              onClick={() => messageDraft && saveDraft(messageDraft)}
            >
              {messageDraft?.isNew ? t("Create invite") : t("Save Message")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box
        border="1px solid"
        borderColor="smBorder"
        rounded="md"
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t("Organization name")}</Th>
                <Th>{t("Recipients")}</Th>
                <Th>{t("Message")}</Th>
                <Th textAlign="center">{t("Invited")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tenderersLoading ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" color="smMuted">
                    <Spinner size="sm" mr="2" />
                    {t("Loading the suppliers who tendered")}
                  </Td>
                </Tr>
              ) : tenderersFailed ? (
                <Tr>
                  <Td colSpan={4}>
                    <ListLoadError
                      message={t(
                        "The suppliers who tendered could not be loaded",
                      )}
                      onRetry={roster.refetchTenderers}
                      isRetrying={roster.tenderersRetrying}
                    />
                  </Td>
                </Tr>
              ) : (
                candidates.length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center" color="smMuted">
                      {t("No invites have been added")}
                    </Td>
                  </Tr>
                )
              )}
              {!tenderersLoading &&
                !tenderersFailed &&
                candidates.map((candidate) => (
                  <InviteRow
                    key={candidate.organizationId}
                    candidate={candidate}
                    roundInvite={roundInviteFor(candidate.organizationId)}
                    canEditMessage={canEditMessages}
                    onOpenMessage={() => openDraftFor(candidate)}
                    onRemove={() =>
                      removeWithConfirmation(candidate.organizationId)
                    }
                    removalLockedBecause={removalLockedBecause}
                    isRejected={roster.rejectedOrganizationIds.includes(
                      candidate.organizationId,
                    )}
                    isReducedOut={roster.reducedOrganizationIds.includes(
                      candidate.organizationId,
                    )}
                    didNotAnswerRound={
                      roster.didNotAnswerPreviousRound?.organizationIds.includes(
                        candidate.organizationId,
                      )
                        ? roster.didNotAnswerPreviousRound.roundNumber
                        : undefined
                    }
                  />
                ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {leavesOutTenderers && (
        <SelectionJustification
          value={justification}
          suggestion={justificationSuggestion}
          onChange={setJustification}
          onBlur={() => void saveJustification()}
          isDisabled={!canEditMessages}
        />
      )}

      <StepNavigation
        onPrevious={previousStep}
        onNext={goNext ? () => void goNext() : undefined}
        nextLabel={nextStepLabel}
        isNextDisabled={nextBlockedBecause != null}
        nextDisabledReason={nextBlockedBecause}
      />
    </Box>
  );
};

const SelectionJustification = ({
  value,
  suggestion,
  onChange,
  onBlur,
  isDisabled,
}: {
  value: string;
  suggestion?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  isDisabled: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <FormControl mt="6" isRequired isInvalid={value.trim() === ""}>
      <FormLabel htmlFor="selection-justification">
        {t("Why not every supplier who tendered is invited")}
      </FormLabel>
      <Textarea
        id="selection-justification"
        backgroundColor="smBackground"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        isDisabled={isDisabled}
      />
      <FormHelperText>
        {t(
          "Dialogue with only some of the suppliers must be justified (FOA § 9-3 (2)). The justification is written in the procurement protocol.",
        )}
      </FormHelperText>
      {suggestion && value.trim() === "" && !isDisabled && (
        <Button
          size="xs"
          variant="link"
          colorScheme="teal"
          mt="1"
          whiteSpace="normal"
          textAlign="left"
          onClick={() => onChange(suggestion)}
        >
          {t("Use suggestion: «{{suggestion}}»", { suggestion })}
        </Button>
      )}
    </FormControl>
  );
};

const InviteRow = ({
  candidate,
  roundInvite,
  canEditMessage,
  onOpenMessage,
  onRemove,
  removalLockedBecause,
  isRejected,
  isReducedOut,
  didNotAnswerRound,
}: {
  candidate: RoundInviteCandidate;
  roundInvite: NegotiationRoundDto["invites"][number] | undefined;
  canEditMessage: boolean;
  onOpenMessage: () => void;
  onRemove: () => void;
  removalLockedBecause?: string;
  isRejected: boolean;
  isReducedOut: boolean;
  didNotAnswerRound?: number;
}) => {
  const { t } = useTranslation();
  const isInvited = !!roundInvite;
  const switchLockedBecause = isRejected
    ? t("The bid is rejected")
    : isReducedOut
      ? t("Taken out of the negotiations by a reduction")
      : isInvited
        ? removalLockedBecause
        : undefined;
  const message = roundInvite?.message ?? "";

  const recipients = isInvited
    ? [
        ...roundInvite.persons.map((person) => person.name),
        ...(roundInvite.emailInvites ?? []).map(
          (emailInvite) => emailInvite.email,
        ),
      ]
    : candidate.recipientLabels;

  return (
    <Tr>
      <Td verticalAlign="top" fontWeight="medium">
        <OrgName organizationId={candidate.organizationId} />
        {!isInvited && !isRejected && !isReducedOut && didNotAnswerRound !== undefined && (
          <Text fontSize="xs" fontWeight="normal" color="smMuted">
            {t("Did not answer round {{number}}, so not invited by default", {
              number: didNotAnswerRound,
            })}
          </Text>
        )}
      </Td>
      <Td verticalAlign="top">
        {recipients.length > 0 ? (
          recipients.map((recipient) => (
            <Text key={recipient} fontSize="sm">
              {recipient}
            </Text>
          ))
        ) : (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      <Td verticalAlign="top">
        {isInvited && canEditMessage ? (
          <Button
            size="sm"
            variant="ghost"
            color={message ? "smText" : "smMuted"}
            onClick={onOpenMessage}
          >
            {message
              ? message.length > 30
                ? `${message.substring(0, 30)}...`
                : message
              : t("Add message")}
          </Button>
        ) : isInvited && message ? (
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {message}
          </Text>
        ) : (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      <Td verticalAlign="top" textAlign="center">
        <Explanation
          enabled={switchLockedBecause !== undefined}
          text={switchLockedBecause}
          shouldWrapChildren
        >
          <Switch
            aria-label={t("Invite the supplier") ?? undefined}
            isDisabled={switchLockedBecause !== undefined}
            isChecked={isInvited}
            colorScheme="teal"
            onChange={(e) => {
              if (e.target.checked) onOpenMessage();
              else onRemove();
            }}
          />
        </Explanation>
      </Td>
    </Tr>
  );
};

// FOA § 16-12 (3): three is the floor these procedures set for the candidates invited to tender.
const FEWEST_SUPPLIERS_FOR_REAL_COMPETITION = 3;

interface PendingReduction {
  organizationId: string;
  reason: string;
}

const ReductionModal = ({
  pending,
  suppliersLeft,
  provision,
  isSaving,
  onChange,
  onCancel,
  onConfirm,
}: {
  pending: PendingReduction | null;
  suppliersLeft: number;
  provision: string | null;
  isSaving: boolean;
  onChange: (pending: PendingReduction) => void;
  onCancel: () => void;
  onConfirm: (pending: PendingReduction) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={!!pending} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Take out of the negotiations")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {pending && (
            <>
              <Text mb="4">
                <OrgName organizationId={pending.organizationId} />{" "}
                {t(
                  "is taken out of the negotiations and is not invited to this or later rounds. The supplier is told at once by email, with your reason, and the reason is written in the procurement protocol.",
                )}
              </Text>
              {suppliersLeft < FEWEST_SUPPLIERS_FOR_REAL_COMPETITION && (
                <Alert status="warning" variant="subtle" rounded="md" mb="4" data-testid="few-suppliers-warning">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    {provision
                      ? t(
                          "Suppliers left in the negotiations after this: {{count}}. The final stage must have enough tenders for real competition, as long as there are enough suitable ones ({{provision}} (3)).",
                          { count: suppliersLeft, provision },
                        )
                      : t(
                          "Suppliers left in the negotiations after this: {{count}}. The final stage must have enough tenders for real competition, as long as there are enough suitable ones.",
                          { count: suppliersLeft },
                        )}
                  </AlertDescription>
                </Alert>
              )}
              <FormControl isRequired>
                <FormLabel htmlFor="reduction-reason">
                  {t("Reason, on the award criteria")}
                </FormLabel>
                <Textarea
                  id="reduction-reason"
                  backgroundColor="smBackground"
                  value={pending.reason}
                  onChange={(e) => onChange({ ...pending, reason: e.target.value })}
                />
                <FormHelperText>
                  {provision
                    ? t(
                        "Say how the tender scored against the award criteria announced. The supplier reads this reason ({{provision}}).",
                        { provision },
                      )
                    : t(
                        "Say how the tender scored against the award criteria announced. The supplier reads this reason.",
                      )}
                </FormHelperText>
              </FormControl>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button
            colorScheme="red"
            isLoading={isSaving}
            isDisabled={!pending?.reason.trim()}
            onClick={() => pending && onConfirm(pending)}
          >
            {t("Take out")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const OrgName = ({ organizationId }: { organizationId: string }) => {
  const { data: org } = useGetOrganizationQuery({ orgId: organizationId });
  return <>{org?.name ?? "…"}</>;
};
